import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { getApiBaseUrl } from './apiBaseUrl';
import api from './axios';

import { getStoredDepartmentView } from '../components/DepartmentSwitcher';

const API_URL = getApiBaseUrl();

const activityApi = axios.create({
  baseURL: `${API_URL}/activity`,
  headers: {
    'Content-Type': 'application/json'
  }
});

activityApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const departmentView = getStoredDepartmentView();
  if (departmentView) {
    config.params = { ...(config.params || {}), departmentView };
  }
  return config;
});

export const useMyActivity = (range = 'week') => {
  return useQuery({
    queryKey: ['my-activity', range],
    queryFn: async () => {
      const { data } = await activityApi.get('/my-activity', {
        params: { range }
      });
      return data;
    }
  });
};

export const useMyCommits = (limit = 50, offset = 0) => {
  return useQuery({
    queryKey: ['my-commits', limit, offset],
    queryFn: async () => {
      const { data } = await activityApi.get('/my-commits', {
        params: { limit, offset }
      });
      return data;
    }
  });
};

export const useMyRepos = () => {
  return useQuery({
    queryKey: ['my-repos'],
    queryFn: async () => {
      const { data} = await activityApi.get('/my-repos');
      return data;
    }
  });
};

export const useMyPullRequests = (range = 'week') => {
  return useQuery({
    queryKey: ['my-prs', range],
    queryFn: async () => {
      const { data } = await activityApi.get('/my-prs', {
        params: { range }
      });
      return data;
    }
  });
};

export const useTeamActivity = (range = 'week') => {
  const departmentView = getStoredDepartmentView();
  return useQuery({
    queryKey: ['team-activity', range, departmentView || 'all'],
    queryFn: async () => {
      const { data } = await activityApi.get('/team', {
        params: { range }
      });
      return data;
    }
  });
};

export const useActivityLeaderboard = (range = 'week', limit = 10) => {
  const departmentView = getStoredDepartmentView();
  return useQuery({
    queryKey: ['activity-leaderboard', range, limit, departmentView || 'all'],
    queryFn: async () => {
      const { data } = await activityApi.get('/leaderboard', {
        params: { range, limit }
      });
      return data;
    }
  });
};

export const useDeveloperActivity = (userId, range = 'week') => {
  return useQuery({
    queryKey: ['developer-activity', userId, range],
    queryFn: async () => {
      const { data } = await activityApi.get(`/developer/${userId}`, {
        params: { range }
      });
      return data;
    },
    enabled: !!userId
  });
};

export const useProjectActivity = (projectId, range = 'month') => {
  return useQuery({
    queryKey: ['project-activity', projectId, range],
    queryFn: async () => {
      const { data } = await activityApi.get(`/project/${projectId}`, {
        params: { range }
      });
      return data;
    },
    enabled: !!projectId
  });
};

// Workload summary for all employees (developers, testers, designers, etc.)
export const useWorkloadSummary = () => {
  const departmentView = getStoredDepartmentView();
  return useQuery({
    queryKey: ['workload-summary', departmentView || 'all'],
    queryFn: async () => {
      const { data } = await api.get('/users/workload-summary');
      return Array.isArray(data) ? data : [];
    },
    staleTime: 30000,
    refetchInterval: 120000,
    retry: 1
  });
};
